import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { MicrophoneIcon } from '@heroicons/react/24/solid'

import { AssistantUiKey } from '../../../../pure-js/libs/assistantMessages'
import { INTERAX_COLORS } from '../../constants/colors'
import { Button } from '../../interaxDesignSystemComponents'
import { TESTIDS } from '../../config/testids'
import { useTranslation } from '../hooks/useIntractionTranslate'

export interface AriaVoicePrimerProps {
  onStartVoice: () => void
  onUseText: () => void
  /** Why the last attempt failed, when there was one. Turns the primer into the retry surface. */
  error?: string
  /** Name the job instead of inviting a chat: in a spoken mode this primer is the only start control. */
  title?: string
  body?: string
  startLabel?: string
}

/**
 * Shown before the microphone has ever been granted, and again whenever a spoken session fails. The
 * first session needs an explicit tap: some browsers dismiss a permission prompt fired on page open.
 */
export const AriaVoicePrimer: React.FC<AriaVoicePrimerProps> = ({
  onStartVoice,
  onUseText,
  error,
  title,
  body,
  startLabel
}) => {
  const { t } = useTranslation()

  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      data-testid={TESTIDS.ARIA_VOICE_PRIMER}
      sx={{
        textAlign: 'center',
        px: { xs: 2, sm: 2.5, md: 3 },
        py: { xs: 2.5, sm: 3, md: 3 },
        borderRadius: 2,
        border: `1px solid ${error ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.white10}`,
        background: INTERAX_COLORS.gradientAssistantVoiceGlow
      }}
    >
      <MicrophoneIcon width={28} height={28} color={INTERAX_COLORS.sun} />
      <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: 16 }}>
        {error ? t(AssistantUiKey.VOICE_UNAVAILABLE) : title || t(AssistantUiKey.VOICE_PRIMER_TITLE)}
      </Typography>
      <Typography
        sx={{ color: error ? INTERAX_COLORS.redError : INTERAX_COLORS.white70, fontSize: 13, lineHeight: 1.5 }}
      >
        {error || body || t(AssistantUiKey.VOICE_PRIMER_BODY)}
      </Typography>
      <Button variant="primary" size="small" onClick={onStartVoice} data-testid={TESTIDS.ARIA_VOICE_PRIMER_START_BTN}>
        {error ? t(AssistantUiKey.VOICE_PRIMER_START) : startLabel || t(AssistantUiKey.VOICE_PRIMER_START)}
      </Button>
      {/* A link, not a second button — talking is the choice being offered. */}
      <Button variant="link" size="small" onClick={onUseText} data-testid={TESTIDS.ARIA_VOICE_PRIMER_TEXT_BTN}>
        {t(AssistantUiKey.VOICE_PRIMER_TYPE_INSTEAD)}
      </Button>
    </Stack>
  )
}

export default AriaVoicePrimer
